import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import CreateForm from "../../../../components/Events/Form/Form";
import { COMPANY_DRAFT_EVENT_STEP_IDS } from "../../../../components/Events/Form/formStepIds";
import { isDraftEvent } from "../../../../components/Events/Event/BasicInfo/paymentStateUtils";
import { UIActionModal } from "../../../../components/ui";
import messages from "../../../../assets/locale/messages";
import * as companyPortalEventsApi from "../../../../network/apis/companyPortalEvents";
import { eventStatuses } from "../../../../utils/Constants";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { showErrorMsg, showSuccessMsg, formatEventStatusTabLabel } from "../../../../utils/Helpers";
import {
  COMPANY_PORTAL_FORM_VARIANT,
  getPortalApiErrorMessage,
  isCompanyPortalEventReadOnly,
  mergePortalEventTypeIntoPayload,
  unwrapPortalEventEnvelope,
} from "../../../../utils/companyPortal/eventApi";
import {
  mergeEventSnapshot,
  useCompanyPortalLookups,
  useLeaveWhenNotDraft,
  useScheduleBounds,
} from "../_eventFormHelpers";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import { getCompanyEventListTab } from "../../../../utils/companyPortal/eventListTabs";

const EditEvent = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: eventId } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];

  const eventIdRef = useRef(eventId ?? null);
  /**
   * Kept out of the load effect's deps: reloading the event on a language switch resets the
   * steps' unsaved edits (PROLINE-4161). The form's options already follow the language.
   */
  const messagesRef = useRef({ events, shared });
  messagesRef.current = { events, shared };
  const [initialEvent, setInitialEvent] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  /** Opened as a draft: the wizard has the Crew Size and Publish & Pay steps. */
  const isDraft = isDraftEvent(initialEvent);
  const isDraftRef = useRef(false);
  isDraftRef.current = isDraft;
  const { leaveIfPublishedMeanwhile } = useLeaveWhenNotDraft({ eventIdRef });
  /** A draft whose save was refused: leave if it was published meanwhile, else show the error. */
  const showSaveError = useCallback(
    async (e) => {
      if (isDraftRef.current && (await leaveIfPublishedMeanwhile())) return;
      showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
    },
    [leaveIfPublishedMeanwhile, shared.generalErrMsg],
  );

  // --- Conflict-check confirmation modal (upcoming/ongoing events) ---
  const [conflictModal, setConflictModal] = useState({
    open: false,
    message: "",
    onConfirm: null, // async () => void — set by whichever handler triggered the check
  });

  const { rolesOptions, requirementsOptions, eventTypeOptions } =
    useCompanyPortalLookups();
  const { scheduleBounds, updateScheduleBounds } = useScheduleBounds();

  // -------------------------------------------------------------------------
  // Load the existing event on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!eventId) return undefined;

    let cancelled = false;

    companyPortalEventsApi
      .getCompanyPortalEvent(eventId)
      .then((res) => {
        if (cancelled) return;
        const unwrapped = unwrapPortalEventEnvelope(res);
        const attrs = unwrapped?.attributes ?? {};
        if (isCompanyPortalEventReadOnly(attrs.status)) {
          const { events: eventsT } = messagesRef.current;
          const statusLabel = formatEventStatusTabLabel(
            attrs.status,
            eventsT.listEvents.eventTabs,
          );
          const msg = eventsT.editEvents.readOnlyEventCannotEditStatus.replace(
            "{status}",
            statusLabel || String(attrs.status ?? ""),
          );
          showErrorMsg(msg);
          history.replace(
            ROUTE_PATHS.companyViewEvent.replace(
              ":id",
              String(unwrapped?.id ?? eventId),
            ),
          );
          return;
        }
        setInitialEvent(attrs);
        updateScheduleBounds(attrs, {});
      })
      .catch((e) => {
        if (cancelled) return;
        showErrorMsg(
          getPortalApiErrorMessage(e, messagesRef.current.shared.generalErrMsg),
        );
        history.replace(ROUTE_PATHS.companyEvents);
      });

    return () => {
      cancelled = true;
    };
  }, [
    eventId,
    updateScheduleBounds,
    history,
  ]);

  // -------------------------------------------------------------------------
  // Always PATCH — event already exists in edit mode
  // -------------------------------------------------------------------------
  const saveEventBody = useCallback(
    async (body) => {
      const id = eventIdRef.current;
      const payload = mergePortalEventTypeIntoPayload(
        body && typeof body === "object" ? { ...body } : {},
        initialEvent,
      );
      const res = await companyPortalEventsApi.updateCompanyPortalEvent({
        id,
        data: payload,
      });
      const unwrapped = unwrapPortalEventEnvelope(res);
      updateScheduleBounds(unwrapped?.attributes ?? {}, payload);
      setInitialEvent((current) => mergeEventSnapshot(current, unwrapped));
      return unwrapped ?? { id, attributes: {} };
    },
    [initialEvent, updateScheduleBounds],
  );

  // Checks for conflicts before saving. If conflicts exist, opens the modal and
  // stores `onConflicted(count)` as the confirm callback — the caller decides
  // what to do after the user confirms. Returns null when the modal is shown.
  const saveEventBodyChecked = useCallback(
    async (body, { forceConflictCount, onConflicted } = {}) => {
      const id = eventIdRef.current;
      const shouldValidate =
        id &&
        [eventStatuses.upcoming, eventStatuses.ongoing].includes(
          initialEvent?.status,
        ) &&
        forceConflictCount == null;

      if (shouldValidate) {
        try {
          const conflictRes =
            await companyPortalEventsApi.getCompanyPortalEventConflictingResources(
              {
                id,
                params: {
                  building_start_at: body?.building_start_at,
                  dismantling_end_at: body?.dismantling_end_at,
                  event_start_at: body?.event_start_at,
                  event_end_at: body?.event_end_at,
                },
              },
            );
          const conflictCount =
            conflictRes?.data?.number_of_conflicting_resources ?? 0;
          if (conflictCount > 0) {
            setConflictModal({
              open: true,
              message: conflictRes.data.message || "",
              onConfirm: () => onConflicted?.(conflictCount),
            });
            return null;
          }
        } catch (e) {
          showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
          return null;
        }
      }

      const finalBody =
        forceConflictCount != null
          ? {
              ...(body ?? {}),
              expected_number_of_conflicting_resources: forceConflictCount,
            }
          : body;

      return saveEventBody(finalBody);
    },
    [initialEvent?.status, saveEventBody, shared.generalErrMsg],
  );

  const handleConflictConfirm = useCallback(async () => {
    const { onConfirm } = conflictModal;
    setConflictModal({ open: false, message: "", onConfirm: null });
    setNextLoading(true);
    try {
      await onConfirm?.();
    } catch (e) {
      showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
    } finally {
      setNextLoading(false);
    }
  }, [conflictModal, shared.generalErrMsg]);

  const handleConflictCancel = useCallback(() => {
    setConflictModal({ open: false, message: "", onConfirm: null });
  }, []);

  // -------------------------------------------------------------------------
  // Handlers forwarded to CreateForm
  // -------------------------------------------------------------------------
  const handlePersistDraft = useCallback(
    async ({ payload }) => {
      if (!payload) return;
      setSaveLoading(true);
      const afterSave = () => {
        showSuccessMsg("eventUpdatedSuccessfully", false);
        queueMicrotask(() => {
          history.push(
            ROUTE_PATHS.companyViewEvent.replace(":id", eventIdRef.current),
          );
        });
      };
      try {
        const persisted = await saveEventBodyChecked(payload, {
          onConflicted: async (count) => {
            const saved = await saveEventBodyChecked(payload, {
              forceConflictCount: count,
            });
            if (saved) afterSave();
          },
        });
        if (persisted === null) return null;
        if (persisted) afterSave();
        return persisted;
      } catch (e) {
        await showSaveError(e);
        return null;
      } finally {
        setSaveLoading(false);
      }
    },
    [history, saveEventBodyChecked, showSaveError],
  );

  const handleAdvanceAfterValidatedStep = useCallback(
    async ({ payload }) => {
      setNextLoading(true);
      try {
        return await saveEventBodyChecked(payload ?? {}, {
          onConflicted: async (count) => {
            await saveEventBodyChecked(payload ?? {}, {
              forceConflictCount: count,
            });
          },
        });
      } catch (e) {
        await showSaveError(e);
        return null;
      } finally {
        setNextLoading(false);
      }
    },
    [saveEventBodyChecked, showSaveError],
  );

  /**
   * Edit mode finish:
   * PATCH only, then redirect to the view page. A draft is not published from here:
   * the company continues to Publish & Pay from the event's Basic Info card.
   */
  const handleFinishSave = useCallback(
    async ({ payload }) => {
      setNextLoading(true);

      const afterSave = async () => {
        showSuccessMsg("eventUpdatedSuccessfully", false);

        queueMicrotask(() => {
          history.push(
            ROUTE_PATHS.companyViewEvent.replace(":id", eventIdRef.current),
          );
        });
      };

      try {
        const persisted = await saveEventBodyChecked(payload ?? {}, {
          onConflicted: async (count) => {
            const saved = await saveEventBodyChecked(payload ?? {}, {
              forceConflictCount: count,
            });
            if (saved) await afterSave();
          },
        });
        if (persisted === null) return false;
        await afterSave();
        return true;
      } catch (e) {
        showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        return false;
      } finally {
        setNextLoading(false);
      }
    },
    [history, initialEvent?.status, saveEventBodyChecked, shared.generalErrMsg],
  );

  /** Publish & Pay: the request was sent; replace, so Back does not reopen the wizard. */
  const handlePaymentSubmitted = useCallback(() => {
    history.replace(ROUTE_PATHS.companyEvents);
  }, [history]);

  const handleCancel = useCallback(() => {
    history.push(
      ROUTE_PATHS.companyViewEvent.replace(":id", eventIdRef.current),
    );
  }, [history]);

  const breadcrumbList = [
    {
      label: events.listEvents.listEventsBreadcrumb,
      path: ROUTE_PATHS.companyEvents,
    },
    {
      label: formatEventStatusTabLabel(
        getCompanyEventListTab(initialEvent),
        events.listEvents?.eventTabs ?? {},
      ),
      path: initialEvent?.status
        ? `${ROUTE_PATHS.companyEvents}?tab=${getCompanyEventListTab(initialEvent)}`
        : ROUTE_PATHS.companyEvents,
    },
    {
      label: initialEvent?.name || "",
      path: eventId
        ? ROUTE_PATHS.companyViewEvent.replace(":id", eventId)
        : ROUTE_PATHS.companyEvents,
    },
    {
      label: events.editEvents.editEventBreadcrumb,
      path: "",
    },
  ];

  return (
    <>
    <Breadcrumbs list={breadcrumbList}   />

      <CreateForm
        variant={COMPANY_PORTAL_FORM_VARIANT}
        isEditMode
        stepIds={isDraft ? COMPANY_DRAFT_EVENT_STEP_IDS : undefined}
        rolesOptions={rolesOptions}
        requirementsOptions={requirementsOptions}
        supervisorsOptions={[]}
        eventTypeOptions={eventTypeOptions}
        eventScheduleBounds={scheduleBounds}
        initialEvent={initialEvent}
        initialStep={location.state?.initialStep}
        requestsPortal="company"
        onPersistDraft={handlePersistDraft}
        onPersistStepContinue={handleAdvanceAfterValidatedStep}
        onFinishPublish={handleFinishSave}
        onPaymentSubmitted={handlePaymentSubmitted}
        // The backend already toasts its reason; only leave if the event moved on.
        onPaymentRequestFailed={() => leaveIfPublishedMeanwhile(null)}
        onCancel={handleCancel}
        draftLoading={saveLoading}
        nextLoading={nextLoading}
      />

      {/* GUARD: conflict confirmation modal — shown when the edited dates overlap an
          ongoing event that shares approved applicants. On confirm the save is re-attempted
          with expected_number_of_conflicting_resources so the backend proceeds with
          auto-cancellation instead of returning a conflict error. */}
      <UIActionModal
        open={conflictModal.open}
        onClose={handleConflictCancel}
        variant="reject"
        iconName="alertCircle"
        showIcon
        title={events.editEvents.conflictModalTitle}
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={shared.confirm}
            onCancel={handleConflictCancel}
            onConfirm={handleConflictConfirm}
            confirmVariant="reject"
          />
        }
      >
        <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
          {conflictModal.message || events.editEvents.conflictModalMessage}
        </Typography>
      </UIActionModal>
    </>
  );
};

export default EditEvent;
